import { Button, Card, CardContent, Divider, Modal, styled, Tab, Tabs, Typography } from "@mui/material";
import { get, toUpper } from "lodash";
import React from "react";
import ReactGA from "react-ga4";
import Autocomplete from "../../../common/js/components/Autocomplete";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import { Close, Search } from "../../../common/js/icons";
import { us_states_options } from "../utils/constants";
import { buildAddress } from "../utils/operations";

const StyledCard = styled(Card)({
    display: "flex",
    marginBottom: "40px",
    border: "1px solid #dddddd",
    boxShadow: "none",
    minHeight: "74px",
    backgroundColor: "#f4f4f4",
});

const StyledSelectedCard = styled(Card)({
    display: "flex",
    marginBottom: "40px",
    border: "1px solid #2C99D2",
    boxShadow: "none",
});

const StyledDetails = styled("div")({
    width: "100%",
});

const StyledContentContainer = styled(CardContent)({
    flex: "1 0 auto",
    height: "100%",
    padding: "16px !important",
});

const StyledContent = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
});

const StyledInstruction = styled("div")({
    marginTop: "24px",
    marginBottom: "24px",
});

const StyledPropertyType = styled(Typography)({
    textTransform: "uppercase",
    fontSize: "13px",
    color: "#9e9e9e",
    marginTop: "-8px",
    marginBottom: "10px",
    fontWeight: "bold",
});

const StyledClose = styled("div")({
    "&:hover": {
        cursor: "pointer",
    },
});

const StyledPaper = styled("div")(({ theme }) => ({
    position: "absolute",
    width: theme.spacing(60),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3),
    borderRadius: "3px",
    "&:focus": {
        outline: "none",
    },
}));

const StyledPropertyAddress = styled(Typography)({
    textTransform: "uppercase",
    fontSize: "13px",
});

const StyledFooter = styled("div")({
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
});

function getModalStyle() {
    const top = 50;
    const left = 50;

    return {
        top: `${top}%`,
        left: `${left}%`,
        transform: `translate(-${top}%, -${left}%)`,
    };
}

const RequestModal = (props) => {
    const {
        autocompleteResults,
        handleSearchSubmit,
        handleShowModal,
        modalType,
        removeSelectedProperty,
        requestListing,
        requestModal,
        selectProperty,
        showModal,
        updateListingRequest,
        updateSearchTerm,
    } = props;

    const errors = requestModal.errors;

    const handleUpdate = (event) => {
        updateListingRequest(event.target.id, event.target.value);
    };

    const sendEventAndRequestListing = (modalType, transactionType, useType, tab, role) => {
        ReactGA.event("Request Sent", {
            requestType: modalType,
            propertyType: useType,
            transactionType: transactionType,
            newOrExisting: tab,
            userRole: role,
        });
        requestListing(modalType, transactionType, useType, tab, role);
    };

    return (
        <Modal aria-labelledby="listing-request-modal" aria-describedby="request-listing" open={showModal}>
            <StyledPaper style={getModalStyle()}>
                <h4 className="modal-title">Select a Property</h4>
                <StyledPropertyType variant="subtitle1" color="textSecondary">
                    {requestModal.useType} {requestModal.transactionType}
                </StyledPropertyType>
                <Tabs value={requestModal.tab} onChange={(e, v) => updateListingRequest("tab", v)}>
                    <Tab value="existing" label="From Existing" />
                    <Tab value="new" label="Request New Property" />
                </Tabs>
                <div>
                    <Divider />
                </div>
                {requestModal.tab === "existing" ? (
                    <React.Fragment>
                        <StyledInstruction>
                            <Typography variant="body1">
                                Search the KM database to find the property associated with your listing.
                            </Typography>
                        </StyledInstruction>
                        <Autocomplete
                            id="property"
                            label={"Property Name"}
                            error={get(errors, `property_id`, null)}
                            itemToString={(i) => (i == null ? "" : get(i, "propertyAttributes.propertyName", ""))}
                            onSelect={(p, b) => selectProperty(p)}
                            placeholder="Search by property name, address, etc."
                            searchFunction={(input) => {
                                if (input !== null && input !== undefined && input.length > 0) {
                                    updateSearchTerm(input, toUpper("property_modal"));
                                    handleSearchSubmit("property_modal");
                                }
                            }}
                            startAdornment={<Search />}
                            suggestions={autocompleteResults}
                            options={{
                                key: "property_id",
                                display: (suggestion) => {
                                    return get(suggestion, "property_name", "");
                                },
                                displaySub: (suggestion) => {
                                    return get(suggestion, "property_address", "");
                                },
                            }}
                        />
                        &nbsp;
                        {requestModal.selectedProperty.propertyKey ? (
                            <StyledSelectedCard data-testid="requestModalSelectedProperty">
                                <StyledDetails>
                                    <StyledContentContainer>
                                        <StyledContent>
                                            <div>
                                                <Typography component="h4" variant="h4">
                                                    {get(
                                                        requestModal,
                                                        "selectedProperty.propertyAttributes.propertyName"
                                                    )}
                                                </Typography>
                                                <StyledPropertyAddress variant="body2" color="textSecondary">
                                                    {buildAddress(
                                                        get(requestModal, "selectedProperty.primaryAddress", {})
                                                    )}
                                                </StyledPropertyAddress>
                                            </div>
                                            <div>
                                                <StyledClose onClick={removeSelectedProperty}>
                                                    <Close />
                                                </StyledClose>
                                            </div>
                                        </StyledContent>
                                    </StyledContentContainer>
                                </StyledDetails>
                                {/* picture to go here  */}
                            </StyledSelectedCard>
                        ) : (
                            <StyledCard></StyledCard>
                        )}
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <div>
                            &nbsp;
                            <BSInput
                                id="newPropertyName"
                                label="Property Name"
                                value={requestModal.newPropertyName}
                                error={get(errors, `newPropertyName`, null)}
                                onChange={handleUpdate}
                                placeholder="Enter property name"
                                disabled={false}
                            />
                        </div>
                        <div>
                            <BSInput
                                id="newPropertyAddress"
                                label="Address"
                                value={requestModal.newPropertyAddress}
                                error={get(errors, `newPropertyAddress`, null)}
                                onChange={handleUpdate}
                                placeholder="Enter address"
                                disabled={false}
                            />
                        </div>
                        <div style={{ display: "flex", gap: "16px" }}>
                            <div>
                                <BSInput
                                    id="newPropertyCity"
                                    label="City"
                                    value={requestModal.newPropertyCity}
                                    error={get(errors, `newPropertyCity`, null)}
                                    onChange={handleUpdate}
                                    placeholder="Enter city"
                                    disabled={false}
                                />
                            </div>
                            <div>
                                <BSSelect
                                    id="newPropertyState"
                                    label="State"
                                    value={requestModal.newPropertyState}
                                    error={get(errors, `newPropertyState`, null)}
                                    onChange={handleUpdate}
                                    placeholder="Select a state"
                                    options={us_states_options}
                                    disabled={false}
                                />
                            </div>
                            <div>
                                <BSInput
                                    id="newPropertyZip"
                                    label="Zip Code"
                                    value={requestModal.newPropertyZip}
                                    error={get(errors, `newPropertyZip`, null)}
                                    onChange={handleUpdate}
                                    placeholder="Enter zip code"
                                    disabled={false}
                                />
                            </div>
                        </div>
                        <div>
                            <BSInput
                                id="newPropertyComments"
                                label="Internal Comments"
                                value={requestModal.newPropertyComments}
                                error={get(errors, `newPropertyComments`, null)}
                                onChange={handleUpdate}
                                placeholder="Comments entered here are only visible inside KMConnect."
                                disabled={false}
                            />
                        </div>
                    </React.Fragment>
                )}
                <StyledFooter>
                    <div>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                handleShowModal(!showModal);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                    <div>
                        {requestModal.tab === "existing" ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() =>
                                    sendEventAndRequestListing(
                                        modalType,
                                        requestModal.transactionType,
                                        requestModal.useType,
                                        requestModal.tab,
                                        props.role
                                    )
                                }
                            >
                                Start
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() =>
                                    sendEventAndRequestListing(
                                        modalType,
                                        requestModal.transactionType,
                                        requestModal.useType,
                                        requestModal.tab,
                                        props.role
                                    )
                                }
                            >
                                Start
                            </Button>
                        )}
                    </div>
                </StyledFooter>
            </StyledPaper>
        </Modal>
    );
};

export default RequestModal;
